import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import { Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { configureStackedBarChartWithoutLegend } from '../../ChartConfiguration/ChartConfiguration';
import { getLipidForBHDXSummary } from '../HedisMetricService';
import LipidForBHDXSummary from '../../../Models/HedisMetrics/LipidForBHDXSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import FilterOptions from '../FilterOptions';
import '../../Widgets/Widgets.css';

const LipidForBHDXSummaries = (props) => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [lipidForBHDXSummary, setLipidForBHDXSummary] = useState<LipidForBHDXSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const {
        lipidForBhDxHealthPlans,
        setLipidForBhDxHealthPlans,
        lipidForBhDxTeam,
        setLipidForBhDxTeam,
        lipidForBhDxDateFilter,
        setLipidForBhDxDateFilter,
        lipidForBhDxFromDate,
        setLipidForBhDxFromDate,
        lipidForBhDxToDate,
        setLipidForBhDxToDate,
        lipidForBhDxIncludePastOpenStatus,
        setLipidForBhDxIncludePastOpenStatus,
        lipidForBhDxShowInActiveTeam,
        setLipidForBhDxShowInActiveTeam,
        setHedisType,
        setHealthPlan,
        setTeam,
        setFilter,
        setFDate,
        setTDate,
        setIncludePastOpenStatus,
        setShowInActiveTeam,
        setHasElevatedFilterAccess,
    } = useHedisMetricsContext();
    const chartHeight = 450;

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartWithoutLegend(
                    'Lipid for BH DX Summary',
                    'Controlled vs Not Controlled',
                    '',
                    'Count',
                    [{ name: '', data: [0] }],
                    [''],
                    null,
                    chartHeight
                )
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (lipidForBhDxDateFilter !== TimePeriodFilterEnum.Custom || (Boolean(lipidForBhDxFromDate) && Boolean(lipidForBhDxToDate))) {
            const changeFilter = async () => {
                let start = lipidForBhDxFromDate !== null ? new Date(lipidForBhDxFromDate).toDateString() : null;
                let end = lipidForBhDxToDate !== null ? new Date(lipidForBhDxToDate).toDateString() : null;
                var teamInfo = Boolean(lipidForBhDxTeam) ? lipidForBhDxTeam : 'All';
                setLipidForBhDxTeam(teamInfo);
                const data = await getLipidForBHDXSummary(
                    lipidForBhDxHealthPlans,
                    teamInfo,
                    lipidForBhDxDateFilter,
                    start,
                    end,
                    lipidForBhDxIncludePastOpenStatus,
                    lipidForBhDxShowInActiveTeam
                );
                setLipidForBHDXSummary(data);
                setIsPrepared(true);
            };
            if ((lipidForBhDxFromDate !== null || lipidForBhDxToDate !== null) && lipidForBhDxToDate < lipidForBhDxFromDate) {
            } else {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Lipid for BH DX Summary',
                        'Controlled vs Not Controlled',
                        '',
                        'Count',
                        [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
                setIsGraphShow(false);
                changeFilter();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        lipidForBhDxHealthPlans,
        lipidForBhDxTeam,
        lipidForBhDxDateFilter,
        lipidForBhDxFromDate,
        lipidForBhDxToDate,
        lipidForBhDxIncludePastOpenStatus,
        lipidForBhDxShowInActiveTeam,
    ]);

    useEffect(() => {
        if (lipidForBHDXSummary && isPrepared) {
            let xAxisValues = [''];
            let yAxisValues = [];
            let yValues = [];
            let yVal;

            yValues = [];
            yVal = {
                y: lipidForBHDXSummary.Controlled,
                name: 'Controlled',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Controlled', data: yValues, pointPadding: 0, color: '#9ec7e8', showInLegend: true });

            yValues = [];
            yVal = {
                y: lipidForBHDXSummary.NotControlled,
                name: 'Not Controlled',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Not Controlled', data: yValues, pointPadding: 0, color: '#6495ED', showInLegend: true });

            yValues = [];
            yVal = {
                y: lipidForBHDXSummary.NotDocumented,
                name: 'Not Documented',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Not Documented', data: yValues, pointPadding: 0, color: '#4682B4', showInLegend: true });

            if (lipidForBHDXSummary.Controlled === 0 && lipidForBHDXSummary.NotControlled === 0 && lipidForBHDXSummary.NotDocumented === 0) {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Lipid for BH DX Summary',
                        'Controlled vs Not Controlled',
                        '',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
            } else {
                setChartOptions(configureStackedBarChartWithoutLegend('Lipid for BH DX Summary', '', '', 'Count', yAxisValues, xAxisValues, navigateToMembers, chartHeight));
            }

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lipidForBHDXSummary, lipidForBhDxHealthPlans, isPrepared]);

    const handleClick = (event) => {
        setShow(!show);
    };

    const navigateToMembers = () => {
        setHealthPlan(lipidForBhDxHealthPlans);
        setTeam(lipidForBhDxTeam);
        setFilter(lipidForBhDxDateFilter);
        setFDate(lipidForBhDxFromDate);
        setTDate(lipidForBhDxToDate);
        setIncludePastOpenStatus(lipidForBhDxIncludePastOpenStatus);
        setShowInActiveTeam(lipidForBhDxShowInActiveTeam);
        setHedisType(HedisMetricsTypeEnum.LipidForBhDx);
        setHasElevatedFilterAccess(props.hasElevatedFilterAccess);
        history(`/provider/metricsummary/hedismemberdetail/LipidForBhDx`);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" color={'primary'} onClick={navigateToMembers} className="pointer">
                            Lipid for BH DX
                        </Typography>
                        <Tooltip
                            open={show}
                            title={
                                <div>
                                    <Typography variant="body2">
                                        For members aged 18-64 who has an ICD 10 of F20.9, F25.0, F25.9, F31.32, F31.2, F31.3, F31.81, F31.89, F31.9
                                    </Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Controlled :
                                    </Typography>

                                    <Typography variant="caption">CPT code 3048F</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Not Controlled :
                                    </Typography>
                                    <Typography variant="caption">Any of the CPT codes 3049F or 3050F</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Not Documented :
                                    </Typography>
                                    <Typography variant="caption">No CPT codes recorded</Typography>
                                </div>
                            }
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton onClick={handleClick}>
                                <InfoIcon sx={{ width: '18px', height: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <FilterOptions
                        selectedTeam={lipidForBhDxTeam}
                        setSelectedTeam={setLipidForBhDxTeam}
                        dateFilter={lipidForBhDxDateFilter}
                        setDateFilter={setLipidForBhDxDateFilter}
                        fromDate={lipidForBhDxFromDate}
                        setFromDate={setLipidForBhDxFromDate}
                        toDate={lipidForBhDxToDate}
                        setToDate={setLipidForBhDxToDate}
                        healthPlan={lipidForBhDxHealthPlans}
                        setHealthPlan={setLipidForBhDxHealthPlans}
                        includePastOpenStatusMembers={lipidForBhDxIncludePastOpenStatus}
                        setIncludePastOpenStatusMembers={setLipidForBhDxIncludePastOpenStatus}
                        hedisType={HedisMetricsTypeEnum.LipidForBhDx}
                        showInActiveTeams={lipidForBhDxShowInActiveTeam}
                        setShowInActiveTeams={setLipidForBhDxShowInActiveTeam}
                        hasElevatedFilterAccess={props.hasElevatedFilterAccess}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default LipidForBHDXSummaries;
